import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {ToolIconDisplay} from 'llm/chat-library/tool-details/ToolIconDisplay';
import {ToolType} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {useToolIcon} from 'llm/chat-library/tool-details/useToolIcon';
import {getWebCitationUrlParts} from 'llm/markdown/LLMMarkdownUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {maybeGetSmartChipTypeFromId} from 'ui-library/chips/SmartChipTypeUtils';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {getAPIIDPrefix} from 'util/APIUtil';
import * as logging from 'util/Logging';

export interface CitationSourceChipProps {
  citationId: string;
  citationNumber: number;
  onCitationClick: (toolId: string, isSourceCitation?: boolean) => void;
  toolCall: ProgressToolCall | undefined;
}

export default function CitationSourceChip({
  citationId,
  citationNumber,
  onCitationClick,
  toolCall,
}: CitationSourceChipProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const getToolIconResolved = useToolIcon();

  const handleClick = React.useCallback((): void => {
    onCitationClick(citationId, true);
  }, [onCitationClick, citationId]);

  const toolDescription = toolCall
    ? getToolDisplayNameFormatted(toolCall.tool, toolCall.args, ToolCallStatus.COMPLETED)
    : 'View citation details';

  const tooltipContent = (
    <div>
      <div>Activity: {toolDescription}</div>
      <div>Citation #{citationNumber}</div>
    </div>
  );

  // For SummarizeDocument, extract document_id from args to render a chip. The
  // document_id is not guaranteed to be an entry (the agent may read other document types,
  // e.g. subtemplates), so only treat it as a chip id when it maps to a known chip type.
  // Anything unrecognized falls back to the plain text chip below.
  const rawDocumentIdValue =
    toolCall?.tool === ToolType.SummarizeDocument || toolCall?.tool === ToolType.ReadBenchlingDocumentLegacy
      ? toolCall.args?.document_id
      : undefined;
  // args is typed `Record<string, unknown>`, so guard that document_id is actually a string
  // before passing it to id helpers (e.g. getAPIIDPrefix) that assume a string.
  const rawDocumentId = typeof rawDocumentIdValue === 'string' ? rawDocumentIdValue : undefined;
  const isRenderableChipId = rawDocumentId != null && maybeGetSmartChipTypeFromId(rawDocumentId) != null;
  const chipDocumentId = isRenderableChipId ? rawDocumentId : undefined;
  if (rawDocumentId != null && !isRenderableChipId) {
    // The agent read a document whose id isn't a renderable chip type (e.g. a subtemplate,
    // `sbtmpl_`). Log it for observability into how often this happens before falling back.
    logging.info(`[llm] Unrenderable citation document id`, {
      extra: {id: rawDocumentId, modelType: getAPIIDPrefix(rawDocumentId)},
    });
  }

  const toolIcon = toolCall ? getToolIconResolved(toolCall.tool) : null;

  const chipText =
    toolIcon != null ? (
      <span className={css(styles.chipTextWithIcon)}>
        <ToolIconDisplay
          icon={toolIcon}
          glyphClassName={css(styles.chipIconGlyph)}
          imgClassName={css(styles.chipIconImg)}
        />
        {toolCall ? toolDescription : ''}
      </span>
    ) : toolCall ? (
      toolDescription
    ) : (
      ''
    );

  return (
    <li className={css(styles.container)} value={citationNumber}>
      <Tooltip text={tooltipContent} placement={FloatingPlacement.TOP}>
        {chipDocumentId != null ? (
          <BenchChip id={chipDocumentId} size={ChipSize.SMALL} onClick={handleClick} />
        ) : (
          <Chip size={ChipSize.SMALL} text={chipText} onClick={handleClick} />
        )}
      </Tooltip>
    </li>
  );
}

export interface WebCitationSourceProps {
  url: string;
}

export function WebCitationSource({url}: WebCitationSourceProps): JSX.Element | null {
  const urlParts = getWebCitationUrlParts(url);
  const cleanUrl = urlParts?.href ?? url;

  const handleClick = React.useCallback((): void => {
    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  }, [cleanUrl]);

  if (urlParts == null) {
    return null;
  }

  return (
    <li className={css(styles.webCitationItem)}>
      <Tooltip text={cleanUrl} placement={FloatingPlacement.TOP}>
        <Chip size={ChipSize.SMALL} text={urlParts.domain} onClick={handleClick} icon="link" />
      </Tooltip>
    </li>
  );
}

const styles = StyleSheet.create({
  container: {
    listStylePosition: 'inside',
  },
  citationNumber: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  chipTextWithIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  chipIconGlyph: {
    fontSize: 12,
    flexShrink: 0,
  },
  chipIconImg: {
    width: 12,
    height: 12,
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  webCitationItem: {
    listStyle: 'none',
  },
});
